import React, { useEffect } from "react";

import StandardButton from "../../../../../../components/Buttons/StandardButton";
import LoadingIcon from "../../../../../../components/Loaders/LoadingIcon";
import ModalWrapper from "../../../../../../components/Modals/ModalWrapper";
import OverCreditBanner from "../../../../../../components/Paywall/OverCreditBanner";
import GearIcon from "../../../../../../resources/route-builder/gear.svg";
import { useRouteBuilder } from "../../context/RouteBuilderContext";
import AdjustHeaderActions from "./AdjustHeaderActions";
import AdjustWorkspace from "./AdjustWorkspace";
import PropertyDetailDrawer from "./PropertyDetailDrawer";

export default function AdjustRouteBuilderModal({
  onClose,
  onOpenConfiguration,
  configurationOpen = false,
}: {
  onClose: () => void;
  onOpenConfiguration: () => void;
  configurationOpen?: boolean;
}) {
  const {
    builderMode,
    isBusy,
    optimizationBlocked,
    optimizationCreditsWarning,
    session,
    selectedPropertyVisitId,
  } = useRouteBuilder();
  const isEdit = builderMode === "edit";

  // Synchronize the full-screen modal with browser document scrolling.
  useEffect(() => {
    const previous = document.documentElement.style.overflow;
    document.documentElement.style.overflow = "hidden";
    return () => {
      document.documentElement.style.overflow = previous;
    };
  }, []);

  return (
    <div
      {...{ inert: configurationOpen ? "" : undefined }}
      aria-hidden={configurationOpen || undefined}
    >
      <ModalWrapper bgOpacity="50">
        <div
          className="fixed inset-0 z-50 flex flex-col bg-slate-50"
          data-testid="route-builder-adjust-modal"
          onClick={(event) => event.stopPropagation()}
        >
          <div className="flex shrink-0 flex-wrap items-center gap-2 border-b border-slate-200 bg-white px-3 py-2 sm:px-4">
            <div className="flex min-w-0 flex-wrap items-center gap-3">
              <h2 className="text-base font-semibold text-snow-primary sm:text-lg">
                {isEdit
                  ? `Editing ${session?.routes.length ?? 0} route(s)`
                  : "Route Builder"}
              </h2>
              {!isEdit && session?.origin !== "blank" ? (
                <StandardButton
                  as="button"
                  testId="route-builder-configuration-button"
                  disabled={isBusy}
                  className="!bg-white !px-3 !py-2 ring-1 ring-inset ring-slate-300"
                  onClick={onOpenConfiguration}
                >
                  <span className="inline-flex items-center gap-1.5 text-slate-600">
                    <img
                      src={GearIcon}
                      alt=""
                      aria-hidden
                      className="h-4 w-4"
                    />
                    Configuration
                  </span>
                </StandardButton>
              ) : null}
            </div>
            <div className="ml-auto flex flex-wrap items-center gap-2">
              <AdjustHeaderActions onCancel={onClose} />
            </div>
          </div>
          {optimizationBlocked || optimizationCreditsWarning ? (
            <div className="shrink-0">
              <OverCreditBanner
                message={
                  optimizationBlocked
                    ? "You've used all route optimization credits for this month."
                    : optimizationCreditsWarning
                }
                linkTo="/admin/settings/billing?show-plans=1"
                linkText={
                  optimizationBlocked
                    ? "Upgrade your plan to optimize more routes."
                    : "Upgrade now"
                }
              />
            </div>
          ) : null}
          <div className="flex min-h-0 flex-1 flex-col overflow-hidden">
            {session == null && isBusy ? (
              <div className="relative flex min-h-0 flex-1 items-center justify-center bg-slate-50">
                <LoadingIcon fixed={false} size={48} />
              </div>
            ) : (
              <AdjustWorkspace />
            )}
          </div>
        </div>
        <PropertyDetailDrawer
          key={selectedPropertyVisitId ?? "no-property-visit"}
        />
      </ModalWrapper>
    </div>
  );
}
